/* eslint-disable max-len */
import { ArgoContext } from '@amzn/awsscm-argo-core';
import { credentialStorage } from '@amzn/awsscm-argo-core/argo/utils/CredentialStorage';
import { FetchResult,Operation } from '@apollo/client'
import { GraphQLErrors, NetworkError } from '@apollo/client/errors';
import { ApolloServerErrorCode } from '@apollo/server/errors';
import KatalLogger from '@katal/logger';
import KatalMetricsPublisher from '@katal/metrics/lib/KatalMetricsPublisher';
import { GraphQLError } from 'graphql';
import {
	StatusCodes
} from 'http-status-codes';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { Nullable } from '../../interfaces/index';

const AGGREGATE_ERROR_METHOD = "AggregateGraphQLErrors";
const AGGREGATE_ERROR_METRIC = "Errors";
const LATENCY_METRIC = "Latency"

export enum ErrorType {
    // GraphQL Error
    REQUEST_TIMEOUT = "RequestTimeout",
    NETWORK_ERROR = "NetworkError",
    // RFXService Error
    BAD_REQUEST = "BadRequest",
    ACCESS_DENIED = "AccessDenied",
    NOT_FOUND = "NotFound",
    CONCURRENT_WRITE = "ConcurrentWrite",
    INTERNAL_ERROR = "InternalError"
}

export enum ErrorSource {
    GRAPHQL = "GraphQL",
    API_GATEWAY = "APIGateway",
    RFX_SERVICE = "RFXService"
}

interface ErrorRecord {
    source: ErrorSource;
    type: ErrorType | ApolloServerErrorCode;
    message: string;
}

interface getErrorRecordsInput {
    graphQLErrors?: GraphQLErrors;
    networkError?: NetworkError;
}

export const getErrorRecords = ({ graphQLErrors, networkError }: getErrorRecordsInput): ErrorRecord[] => {
    const records: ErrorRecord[] = [];
    if (!isEmpty(networkError)) {
        records.push({ source: ErrorSource.GRAPHQL, type: ErrorType.NETWORK_ERROR, message: networkError.message })
    }

    graphQLErrors?.forEach( (graphQLError: GraphQLError) => {

        if (isGraphQLValidationError(graphQLError)) {
            records.push({ source: ErrorSource.GRAPHQL, type: ApolloServerErrorCode.GRAPHQL_VALIDATION_FAILED, message: graphQLError.message });
        }

        if (isAPIGatewatAccessDenied(graphQLError)) {
            records.push({ source: ErrorSource.API_GATEWAY, type: ErrorType.ACCESS_DENIED, message: graphQLError.message });
        }

        if (isRFXServiceBadRequest(graphQLError)) {
            records.push({ source: ErrorSource.RFX_SERVICE, type: ErrorType.BAD_REQUEST, message: graphQLError.message });
        }

        if (isRFXServiceAccessDenied(graphQLError)) {
            records.push({ source: ErrorSource.RFX_SERVICE, type: ErrorType.ACCESS_DENIED, message: graphQLError.message });
        }

        if (isRFXServiceNotFound(graphQLError)) {
            records.push({ source: ErrorSource.RFX_SERVICE, type: ErrorType.NOT_FOUND, message: graphQLError.message });
        }

        if (isRFXServiceConcurrentWrite(graphQLError)) {
            records.push({ source: ErrorSource.RFX_SERVICE, type: ErrorType.CONCURRENT_WRITE, message: graphQLError.message });
        }

        if (isRFXServiceInternalError(graphQLError)) {
            records.push({ source: ErrorSource.RFX_SERVICE, type: ErrorType.INTERNAL_ERROR, message: graphQLError.message });
        }
    });
    return records;
};

interface handleResponseInput {
    argoContext: ArgoContext;
    operation: Operation;
    response: FetchResult;
}

export const handleResponse = ({ argoContext, operation, response }: handleResponseInput): void => {
    const { initialMetricsPublisher, logger } = argoContext;
    const { operationName } = operation;
    const { data, errors } = response;

    const duration = getDuration(operation);

    const publisher =
        initialMetricsPublisher.newChildActionPublisherForMethod(operationName);

    if (data) publisher.publishTimerMonitor(LATENCY_METRIC, duration);

    if (!errors) {
        const aggPublisher =
            initialMetricsPublisher.newChildActionPublisherForMethod(
                AGGREGATE_ERROR_METHOD
            );
        aggPublisher.publishCounterMonitor(ErrorType.ACCESS_DENIED, 0);
        aggPublisher.publishCounterMonitor(ErrorType.INTERNAL_ERROR, 0);
        aggPublisher.publishCounterMonitor(AGGREGATE_ERROR_METRIC, 0);
        publisher.publishCounterMonitor(AGGREGATE_ERROR_METRIC, 0);
    }
};

interface publishErrorMetricInput {
    records: ErrorRecord[];
    publisher: KatalMetricsPublisher;
    aggPublisher: KatalMetricsPublisher;
    duration: number;
}

/**
 * Publish special error metrics for individual query
 */
const publishErrorMetric = ({ records, publisher, aggPublisher, duration }: publishErrorMetricInput): void => {
    if (records.length === 0 ) return;
    records.forEach( record => {
        switch(record.type) {
            case ErrorType.REQUEST_TIMEOUT:
                publisher.publishTimerMonitor(LATENCY_METRIC, duration);
                break;
        }
        publisher.publishCounterMonitor([record.source, record.type].join(":"), 1);
    });
    publisher.publishCounterMonitor(AGGREGATE_ERROR_METRIC, 1);
    aggPublisher.publishCounterMonitor(AGGREGATE_ERROR_METRIC, 1);
};

interface logErrorForOperationInput {
    logger: KatalLogger;
    userUniqueId: string;
    operation: Operation;
    graphQLErrors?: GraphQLErrors;
    networkError?: NetworkError;
    duration: number;
}

const logErrorForOperation = (
    {logger, userUniqueId, operation, graphQLErrors, networkError, duration}: logErrorForOperationInput)
    : void => {
        const { operationName } = operation;
        const toLog = {
            userId: userUniqueId,
            location: window.location.host + window.location.pathname,
            xRayTraceId: getXRayTraceId(operation),
            duration,
            graphQLErrors: graphQLErrors,
            networkError: networkError
        };
        logger.error(`RFXApp Error in ${operationName} request`, toLog);
}

interface handleErrorsInput {
    argoContext: ArgoContext;
    operation: Operation;
    graphQLErrors?: GraphQLErrors;
    networkError?: NetworkError;
}

export const handleErrors = ({
    argoContext,
    operation,
    graphQLErrors,
    networkError,
}: handleErrorsInput) => {
    const { initialMetricsPublisher, logger } = argoContext;
    const { operationName } = operation;
    const userUniqueId = credentialStorage.getUserUniqueId();

    const publisher =
        initialMetricsPublisher.newChildActionPublisherForMethod(operationName);
    const aggPublisher =
        initialMetricsPublisher.newChildActionPublisherForMethod(
            AGGREGATE_ERROR_METHOD
        );

    const records: ErrorRecord[] = getErrorRecords({ graphQLErrors, networkError });
    const duration = getDuration(operation);
    publishErrorMetric({ records, publisher, aggPublisher, duration });
    logErrorForOperation({ logger, userUniqueId, operation, graphQLErrors, networkError, duration});
};

const isGraphQLValidationError = (error: GraphQLError): boolean => {
    return error.extensions?.code === ApolloServerErrorCode.GRAPHQL_VALIDATION_FAILED
}

const isAPIGatewatAccessDenied = (error: GraphQLError): boolean => {
    const exception = error.extensions?.exception as any;
    return exception?.statusCode === StatusCodes.FORBIDDEN ||
        exception?.code === "AccessDeniedException" ||
        exception?.message?.includes("is not authorized to perform:");
}

const isRFXServiceBadRequest = (error: GraphQLError): boolean => {
    const exception = error.extensions?.exception as any;
    return exception?.statusCode === StatusCodes.BAD_REQUEST ||
        exception?.code === "com.aws.scm.rfxservice.rest.InvalidParameterException";
}

const isRFXServiceAccessDenied = (error: GraphQLError): boolean => {
    const exception = error.extensions?.exception as any;
    return exception?.statusCode === StatusCodes.FORBIDDEN ||
        exception?.code === "com.aws.scm.rfxservice.rest.AccessDeniedException";
}
const isRFXServiceNotFound = (error: GraphQLError): boolean => {
    const exception = error.extensions?.exception as any;
    return exception?.statusCode === StatusCodes.NOT_FOUND ||
        exception?.code === "com.aws.scm.rfxservice.rest.NotFoundException";
}

const isRFXServiceConcurrentWrite = (error: GraphQLError): boolean => {
    const exception = error.extensions?.exception as any;
    return exception?.statusCode === StatusCodes.CONFLICT ||
        exception?.code === "com.aws.scm.rfxservice.rest.ConcurrentWriteException";
}

const isRFXServiceInternalError = (error: GraphQLError): boolean => {
    const regex: RegExp = /^5\d{2}$/;
    const exception = error.extensions?.exception as any;
    return regex.test(String(exception?.statusCode));
}

const getDuration = (operation: Operation): number =>
    Math.round(performance.now() - operation.getContext().start);

const getXRayTraceId = (operation: Operation): Nullable<string> => {
    const headers = get(operation.getContext(), 'response.headers');
    if (!headers) return null;

    const xRayTraceId = headers.get('x-amzn-trace-id');
    if (!xRayTraceId) return null;

    // Parse out non-ID text from header
    return xRayTraceId.replace(/(Root=)|(;Sampled=1)/g, '');
};